import Udesk from 'Udesk';
import React, { FC, useState } from 'react';
import { useRequest } from 'ahooks';
import styled from 'styled-components';
import { Button, Divider, message, Space, Switch, Tabs } from 'udesk-ui';
import { TabsProps } from 'udesk-ui/lib/tabs';
import Page from 'src/components/common/udesk-qa-page-layout';
import MassageConfiguration, {
    TemplateProps as MassageConfigurationProps,
} from 'src/components/pages/components/massage-configuration-v2';
import { AutomaticPushConfig } from 'src/pages/tasks/manage/template/automatic-add/component';
import { getVocAutoMateTasksByTaskType } from 'src/api/vocAutoMateTasks/{taskType}';
import { putVocAutoMateTasks } from 'src/api/vocAutoMateTasks';
import { getVocAutoMateTasksMonitorByTaskType } from 'src/api/vocAutoMateTasks/monitor/{taskType}';
import { postVocAutoMateTasksMonitor } from 'src/api/vocAutoMateTasks/monitor';
import UdeskLocales from 'UdeskLocales';

const Wrapper = styled.div`
    padding: 16px;
`;

const Content = styled.div`
    margin-bottom: 16px;
`;

const TabPaneWrapper = styled.div`
    padding: 16px;
    background-color: #efefef;
`;

type PushConfig = MassageConfigurationProps['value'] & {
    _id: string;
};

interface TemplateProps {}

const Template: FC<TemplateProps> = (props) => {
    const [pushSwitch, setPushSwitch] = useState(false);

    const { run: getStatus } = useRequest(
        () => getVocAutoMateTasksByTaskType({ segments: { taskType: 8 } }),
        {
            onSuccess: (res) => {
                setPushSwitch(res.data?.[8] === 1);
            },
        }
    );

    const onPushSwitchChange = (checked: boolean) => {
        setPushSwitch(checked);

        putVocAutoMateTasks({
            taskTypes: [8],
            taskStatus: checked ? 1 : 0,
        }).then(() => {
            message.success(
                /* 保存成功 */ UdeskLocales['current'].pages.conversationAnalysis.planManage
                    .automaticTriggerMessage.index.saveSuccessful
            );
            getStatus();
        });
    };

    const [pushConfigList, setPushConfigList] = useState<PushConfig[]>();

    useRequest(() => getVocAutoMateTasksMonitorByTaskType({ segments: { taskType: 8 } }), {
        onSuccess: (res) => {
            const configDefault = AutomaticPushConfig({
                interfacePushType: Udesk.enums.sdkPushType.dingDing.id,
            });

            setPushConfigList(
                res.data?.map((config) => {
                    return {
                        _id: Udesk.utils.string.uuid(),
                        name: config.monitorConf?.name,
                        inspectDataSource: config.monitorConf?.inspectDataSource,
                        baseFilter: config.baseFilter,
                        eventSources: config.monitorConf?.eventSources,
                        pushMethod: {
                            pushConf:
                                config.monitorConf?.pushConf ||
                                configDefault.monitorConf.pushConf?.filter?.((c) =>
                                    [
                                        Udesk.enums.messagePushType.system.id,
                                        Udesk.enums.messagePushType.interfacePush.id,
                                        Udesk.enums.messagePushType.email.id,
                                    ].includes((c as any).actionType)
                                ),
                            checkedTypes: config.monitorConf?.pushConf
                                ?.filter((c) => c.enabled === 1)
                                ?.map((c) => c.actionType!),
                        },
                    };
                })
            );
        },
    });

    const onPushConfigTabsEdit: TabsProps['onEdit'] = (e, action) => {
        if (action === 'add') {
            const config = AutomaticPushConfig({
                interfacePushType: Udesk.enums.sdkPushType.dingDing.id,
            });

            setPushConfigList((list) => [
                ...(list || []),
                {
                    _id: Udesk.utils.string.uuid(),
                    pushMethod: {
                        pushConf: config.monitorConf.pushConf?.filter?.((c) =>
                            [
                                Udesk.enums.messagePushType.system.id,
                                Udesk.enums.messagePushType.interfacePush.id,
                                Udesk.enums.messagePushType.email.id,
                            ].includes((c as any).actionType)
                        ),
                    },
                },
            ]);
        } else if (action === 'remove') {
            setPushConfigList((list) => (list || []).filter((config) => config._id !== e));
        }
    };

    const onMassageConfigurationChange = (id: string, v: MassageConfigurationProps['value']) => {
        setPushConfigList((list) =>
            list?.map((config) => {
                if (config._id === id) {
                    return {
                        ...config,
                        ...v,
                    };
                } else {
                    return config;
                }
            })
        );
    };

    const onSave = () => {
        if (pushConfigList?.length) {
            postVocAutoMateTasksMonitor(
                pushConfigList.map((config) => {
                    return {
                        taskType: 8,
                        baseFilter: config.baseFilter,
                        monitorConf: {
                            messageType: 2,
                            name: config.name,
                            inspectDataSource: config.inspectDataSource,
                            eventSources: config.eventSources,
                            pushConf: config.pushMethod?.pushConf,
                        },
                    };
                })
            ).then(() => {
                message.success(
                    /* 保存成功 */ UdeskLocales['current'].pages.conversationAnalysis.planManage
                        .automaticTriggerMessage.index.saveSuccessful
                );
            });
        }
    };

    return (
        <Page
            pageBodyClassName="automatic-trigger-message-page"
            title={
                /* 自动触发消息 */ UdeskLocales['current'].pages.conversationAnalysis.planManage
                    .automaticTriggerMessage.index.autoTriggerMessage
            }
        >
            <Wrapper>
                <Space>
                    <div>
                        {/* 自动推送 */}
                        {
                            UdeskLocales['current'].pages.conversationAnalysis.planManage
                                .automaticTriggerMessage.index.automaticPush
                        }
                    </div>
                    <Switch checked={pushSwitch} onChange={(e) => onPushSwitchChange(e)} />
                </Space>

                <Divider />

                <Content>
                    <Tabs
                        type="editable-card"
                        addIcon={
                            <div style={{ padding: 8 }}>
                                {/* 新建推送 */}
                                {
                                    UdeskLocales['current'].pages.conversationAnalysis.planManage
                                        .automaticTriggerMessage.index.newPush
                                }
                            </div>
                        }
                        onEdit={(e, action) => onPushConfigTabsEdit(e, action)}
                    >
                        {pushConfigList?.map((item, index) => (
                            <Tabs.TabPane
                                key={item._id}
                                tab={`${
                                    /* 推送 */ UdeskLocales['current'].pages.conversationAnalysis
                                        .planManage.automaticTriggerMessage.index.push
                                }${index + 1}`}
                                closable={true}
                            >
                                <TabPaneWrapper>
                                    <MassageConfiguration
                                        value={item}
                                        onChange={(v) => onMassageConfigurationChange(item._id, v)}
                                        querryType="planManage"
                                    />
                                </TabPaneWrapper>
                            </Tabs.TabPane>
                        ))}
                    </Tabs>
                </Content>

                <div>
                    {pushConfigList?.length ? (
                        <Button type="primary" onClick={() => onSave()}>
                            {/* 保存 */}
                            {
                                UdeskLocales['current'].pages.conversationAnalysis.planManage
                                    .automaticTriggerMessage.index.preserve
                            }
                        </Button>
                    ) : null}
                </div>
            </Wrapper>
        </Page>
    );
};

export default Template;
